<script lang="ts">
  import { browser } from '$app/environment'
  import { page } from '$app/stores'
  import { utils } from '$lib/utils'
  import {
    CircularProgress,
    type Language,
    type Theme,
  } from '@bruce/components'
  import { createEventDispatcher, onMount } from 'svelte'
  import { PUBLIC_CLOUDFLARE_TURNSTILE_KEY } from '$env/static/public'
  import type { RenderParameters } from 'turnstile-types'

  export let appearance: RenderParameters['appearance'] = 'always'

  const turnstileScript =
    'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
  $: lang = $page.params.lang as Language
  let defaultTheme: Theme = 'light'
  let mounted = false
  let loaded = typeof window !== 'undefined' && 'turnstile' in window
  let hidden = appearance === 'interaction-only'

  const dispatch = createEventDispatcher<{
    'turnstile-callback': { token: string }
    'turnstile-error': object
    'turnstile-expired': object
    'turnstile-timeout': object
  }>()

  if (browser) {
    const prefersDark =
      window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches

    const storedTheme = utils.theme.get()

    if (storedTheme === 'dark' || (!storedTheme && prefersDark)) {
      defaultTheme = 'dark'
    }
  }

  onMount(() => {
    mounted = true

    return () => {
      mounted = false
    }
  })

  function turnstile(element: HTMLElement) {
    const id = window.turnstile.render(element, {
      sitekey: PUBLIC_CLOUDFLARE_TURNSTILE_KEY,
      appearance,
      language: lang,
      theme: defaultTheme,
      retry: 'never',
      'before-interactive-callback': () => {
        hidden = false
      },
      'error-callback': () => {
        dispatch('turnstile-error', {})
      },
      'expired-callback': () => {
        dispatch('turnstile-expired', {})
      },
      'timeout-callback': () => {
        dispatch('turnstile-timeout', {})
      },
      callback: (token: string) => {
        dispatch('turnstile-callback', { token })
      },
    })

    return {
      destroy: () => {
        window.turnstile.remove(id)
      },
    }
  }
</script>

<svelte:head>
  {#if mounted && !loaded}
    <script
      src={turnstileScript}
      on:load={() => (loaded = true)}
      async
    ></script>
  {/if}
</svelte:head>

<div class:height={!hidden}>
  {#if loaded && mounted}
    <div class="turnstile" use:turnstile></div>
  {:else if !hidden}
    <CircularProgress indeterminate />
  {/if}
</div>

<style lang="scss">
  div {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;

    &.height {
      height: 65px;
    }
  }
</style>
